import { convertEmptyStringToUndefinedFromObject } from '@/utils/object';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { omit } from 'es-toolkit';
import i18n from 'i18next';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_OVERCHARGE_CLAIMS_TABLE_VIEW_COLUMNS } from '../../../contants/ClaimsEnums';
import { getErrorMessageFromAPI } from '../../../utilz';
import { formateWithMoment } from '../../../utilz/dates';
import { setAppLoader } from '../../common/CommonActions';
import {
  applyClaimRecoveryPercentInError,
  applyClaimRecoveryPercentInSuccess,
  createOverchargeClaimInError,
  createOverchargeClaimInSuccess,
  getOCClaimItemsInError,
  getOCClaimItemsInSuccess,
  getOCClaimNotesInError,
  getOCClaimNotesInSuccess,
  getOCClaimSettlementsInError,
  getOCClaimSettlementsInSuccess,
  getOCClaimsSortingFieldsInError,
  getOCClaimsSortingFieldsInSuccess,
  getOCClaimsTableViewColumnsInError,
  getOCClaimsTableViewColumnsInSuccess,
  getOverChargeClaimDetailsInError,
  getOverChargeClaimDetailsInSuccess,
  getOverChargeClaimEmailDetailsInError,
  getOverChargeClaimEmailDetailsInSuccess,
  getOverChargeClamsListInError,
  getOverChargeClamsListInProgress,
  getOverChargeClamsListInSuccess,
  getOverChargeItemsFromExcelInError,
  getOverChargeItemsFromExcelInSuccess,
  saveOCClaimsTableViewColumnsInError,
  saveOCClaimsTableViewColumnsInSuccess,
  sendOverChargeClaimPdfToCarrierInError,
  sendOverChargeClaimPdfToCarrierInSuccess,
  setOverchargeList,
  setSelectedOverChargeClaims,
} from './OverChargeActions';
import {
  applyClaimRecoveryPercentEffect,
  deleteOverChargeClaimsEffect,
  getOCClaimItemsEffect,
  getOCClaimNotesEffect,
  getOCClaimSettlementsEffect,
  getOCClaimTableViewColumn,
  getOverChargeClaimDetailsEffect,
  getOverChargeClaimEmailDetailsEffect,
  getOverChargeItemsFromExcelEffect,
  getOverChargeSortingFieldsTypes,
  getShipmentListEffect,
  saveOCClaimTableViewColumn,
  saveOverChargeClaimDetailsEffect,
  sendOverChargeClaimPdfToCarrierEffect,
} from './OverChargeEffects';
import OverChargeClaimsTypes from './OverChargeTypes';

function* getClaimsList() {
  const claimsFilters = yield select((state) => state.overChargeClaims.claimsFilters);
  try {
    const param = {
      ...convertEmptyStringToUndefinedFromObject(claimsFilters),
      StatusCodes: claimsFilters.StatusCodes ? claimsFilters.StatusCodes.join(',') : '-1',
      StartDate: claimsFilters.StartDate ? formateWithMoment(claimsFilters.StartDate, 'YYYY-MM-DD') : '',
      EndDate: claimsFilters.EndDate ? formateWithMoment(claimsFilters.EndDate, 'YYYY-MM-DD') : '',
    };
    const { data } = yield call(getShipmentListEffect, omit(param, ['byAge']));
    const claimsData = {
      // biome-ignore lint/complexity/useLiteralKeys: <explanation>
      claimsData: data['item1'] ? data['item1'] : data['claimsData'],
      // biome-ignore lint/complexity/useLiteralKeys: <explanation>
      ...(data['item2'] ? data['item2'] : data['footerData']),
    };

    yield put(getOverChargeClamsListInSuccess({ data: claimsData }));
  } catch (e) {
    console.log(e);
    if (e?.code !== 'ERR_CANCELED') {
      yield put(getOverChargeClamsListInError({ message: getErrorMessageFromAPI(e) }));
    }
  }
}

function* deleteClaims({ payload: { resetSelectedList, ...payload } }) {
  try {
    yield put(setAppLoader(true));
    yield call(deleteOverChargeClaimsEffect, payload);

    if (resetSelectedList) {
      yield put(setSelectedOverChargeClaims([]));
    }
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: i18n.t('translation:default.notification.claimsDeleted'),
    });
    yield put(setOverchargeList({ data: [], progressing: true }));
    yield put(getOverChargeClamsListInProgress());
  } catch {
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: i18n.t('translation:default.notification.claimsDeletedFailed'),
    });
  }
}

function* getClaimDetailsWatcher({ payload }) {
  try {
    const { data } = yield call(getOverChargeClaimDetailsEffect, payload);
    yield put(getOverChargeClaimDetailsInSuccess({ data }));
  } catch (e) {
    if (e?.code !== 'ERR_CANCELED') {
      yield put(
        getOverChargeClaimDetailsInError({
          message: getErrorMessageFromAPI(e, i18n.t('translation:default.notification.claimsDetailsFailed')),
        }),
      );
    }
  }
}

function* updateClaimDetailsWatcher({
  payload: { handleSuccess, section, pageIndex, isRefetchDetails = false, ...payload },
}) {
  try {
    yield put(setAppLoader(true));
    yield call(saveOverChargeClaimDetailsEffect, payload);

    if (isRefetchDetails) {
      switch (section) {
        case 'COMMENTS': {
          const { data } = yield call(getOCClaimNotesEffect, {
            fbOcKey: payload.fbOcKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getOCClaimNotesInSuccess({ ...data, removeOld: true }));
          break;
        }

        case 'SETTLEMENTS': {
          const { data } = yield call(getOCClaimSettlementsEffect, {
            fbOcKey: payload.fbOcKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getOCClaimSettlementsInSuccess({ ...data, removeOld: true }));
          break;
        }

        case 'ITEMS': {
          const { data } = yield call(getOCClaimItemsEffect, {
            fbOcKey: payload.fbOcKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getOCClaimItemsInSuccess({ ...data, removeOld: true }));
          break;
        }

        default: {
          const { data } = yield call(getOverChargeClaimDetailsEffect, payload.fbOcKey);
          yield put(getOverChargeClaimDetailsInSuccess({ data }));
          break;
        }
      }
    }
    if (handleSuccess) {
      handleSuccess();
    }
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Claims details updated successfully.',
    });
  } catch {
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Claims details failed to update.',
    });
  }
}

function* sendClaimPdfToCarrierWatcher({ payload: { handleDone, ...payload } }) {
  try {
    const { data } = yield call(sendOverChargeClaimPdfToCarrierEffect, payload);
    yield put(sendOverChargeClaimPdfToCarrierInSuccess({ data }));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: i18n.t('translation:default.fields.transportationProvider.email'),
    });
    if (handleDone) {
      handleDone();
    }
  } catch (e) {
    const errorMessage = getErrorMessageFromAPI(
      e,
      i18n.t('translation:default.fields.transportationProvider.emailError'),
    );
    yield put(
      sendOverChargeClaimPdfToCarrierInError({
        message: errorMessage,
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: errorMessage,
    });
  }
}

function* getClaimEmailDetailsWatcher({ payload }) {
  try {
    const { data } = yield call(getOverChargeClaimEmailDetailsEffect, payload);
    yield put(getOverChargeClaimEmailDetailsInSuccess({ data: data.claimEmails }));
  } catch (e) {
    if (e?.code !== 'ERR_CANCELED') {
      yield put(
        getOverChargeClaimEmailDetailsInError({
          message: getErrorMessageFromAPI(e, 'Failed to get claim email details'),
        }),
      );
    }
  }
}

function* getItemsFromExcelWatcher({ payload: { handleSuccess, ...payload } }) {
  try {
    yield put(setAppLoader(true));
    const { data } = yield call(getOverChargeItemsFromExcelEffect, payload);
    yield put(getOverChargeItemsFromExcelInSuccess({ data }));
    yield put(setAppLoader(false));
    if (handleSuccess) {
      handleSuccess();
    }
  } catch (e) {
    yield put(setAppLoader(false));
    if (e?.code !== 'ERR_CANCELED') {
      yield put(
        getOverChargeItemsFromExcelInError({
          message: getErrorMessageFromAPI(e, 'Failed to get items from excel sheet'),
        }),
      );
    }
  }
}

function* createClaimWatcher({ payload: { handleSuccess, ...payload } }) {
  try {
    yield put(setAppLoader(true));
    const { data } = yield call(saveOverChargeClaimDetailsEffect, payload);
    yield put(createOverchargeClaimInSuccess({ data }));
    if (handleSuccess) {
      handleSuccess(data);
    }
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully created claim.',
    });
  } catch (e) {
    yield put(setAppLoader(false));
    if (e?.code !== 'ERR_CANCELED') {
      yield put(
        createOverchargeClaimInError({
          message: getErrorMessageFromAPI(e, 'Failed to create Claim'),
        }),
      );
    }
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to create Claim',
    });
  }
}

function* getTableViewColumnsWatcher() {
  try {
    const { data } = yield call(getOCClaimTableViewColumn);
    let columns = DEFAULT_OVERCHARGE_CLAIMS_TABLE_VIEW_COLUMNS;
    if (data?.length > 0) {
      columns = data.map(({ fieldReferenceName, fieldReferenceValue }) => ({
        columnName: fieldReferenceName,
        columnValue: fieldReferenceValue,
      }));
    }
    columns = columns.map((row) => ({ ...row, id: uuidv4() }));

    yield put(
      getOCClaimsTableViewColumnsInSuccess({
        data: columns,
      }),
    );
  } catch (e) {
    yield put(
      getOCClaimsTableViewColumnsInError({
        message: getErrorMessageFromAPI(e, 'Failed to get Overcharge claims sorting fields'),
      }),
    );
  }
}

function* getSortingFieldsWatcher() {
  try {
    const { data } = yield call(getOverChargeSortingFieldsTypes);
    yield put(
      getOCClaimsSortingFieldsInSuccess({
        data,
      }),
    );
  } catch (e) {
    yield put(
      getOCClaimsSortingFieldsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* saveTableViewColumns({ payload: { handleDone, ...payload } }) {
  try {
    // biome-ignore lint/correctness/noUnusedVariables: <explanation>
    const { data } = yield call(saveOCClaimTableViewColumn, {
      gridName: 'OverChargeClaims',
      columnOptions: payload.data.map(({ columnName, columnValue }) => ({
        fieldReferenceName: columnName,
        fieldReferenceValue: columnValue,
      })),
    });
    yield put(
      getOCClaimsTableViewColumnsInSuccess({
        data: payload.data,
      }),
    );
    yield put(
      saveOCClaimsTableViewColumnsInSuccess({
        data: payload.data,
      }),
    );
    if (handleDone) {
      handleDone();
    }
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully saved  Overcharge claims columns Options',
    });
  } catch (e) {
    yield put(
      saveOCClaimsTableViewColumnsInError({
        message: getErrorMessageFromAPI(e, 'Failed to saved  Overcharge claims columns Options'),
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to saved  Overcharge claims columns Options',
    });
  }
}

function* getClaimNotesWatcher({ payload }) {
  try {
    const { data } = yield call(getOCClaimNotesEffect, payload);
    yield put(getOCClaimNotesInSuccess(data));
  } catch (e) {
    yield put(
      getOCClaimNotesInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* getClaimSettlementsWatcher({ payload }) {
  try {
    const { data } = yield call(getOCClaimSettlementsEffect, payload);
    yield put(getOCClaimSettlementsInSuccess(data));
  } catch (e) {
    yield put(
      getOCClaimSettlementsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* getClaimItemsWatcher({ payload }) {
  try {
    const { data } = yield call(getOCClaimItemsEffect, payload);
    yield put(getOCClaimItemsInSuccess(data));
  } catch (e) {
    yield put(
      getOCClaimItemsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}
function* applyClaimRecoveryPercentWatcher({ payload: { pageIndex, ...payload } }) {
  try {
    const { data } = yield call(applyClaimRecoveryPercentEffect, payload);
    const { data: itemsData } = yield call(getOCClaimItemsEffect, {
      pageIndex: 1,
      pageSize: pageIndex * 10,
      fbOcKey: payload.fbOcKey,
    });
    yield put(getOCClaimItemsInSuccess({ ...itemsData, removeOld: true }));
    yield put(applyClaimRecoveryPercentInSuccess(data));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Claim Recovery Applied Successfully',
    });
  } catch (e) {
    yield put(
      applyClaimRecoveryPercentInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}
export default function* OverChargeClaimsSaga() {
  yield takeEvery(OverChargeClaimsTypes.GET_OVERCHARGE_LIST_PROGRESS, getClaimsList);
  yield takeLatest(OverChargeClaimsTypes.DELETE_OVERCHARGE_CLAIM_PROGRESS, deleteClaims);
  yield takeLatest(OverChargeClaimsTypes.GET_OVERCHARGE_CLAIM_DETAILS_PROGRESS, getClaimDetailsWatcher);
  yield takeLatest(OverChargeClaimsTypes.UPDATE_OVERCHARGE_CLAIM_PROGRESS, updateClaimDetailsWatcher);
  yield takeLatest(OverChargeClaimsTypes.SEND_CLAIM_PDF_TO_CARRIER_PROGRESS, sendClaimPdfToCarrierWatcher);
  yield takeLatest(OverChargeClaimsTypes.GET_CLAIM_EMAIL_HISTORY_PROGRESS, getClaimEmailDetailsWatcher);
  yield takeLatest(OverChargeClaimsTypes.GET_ITEMS_FROM_EXCEL_PROGRESS, getItemsFromExcelWatcher);
  yield takeLatest(OverChargeClaimsTypes.CREATE_OVERCHARGE_CLAIM_PROGRESS, createClaimWatcher);
  yield takeLatest(OverChargeClaimsTypes.GET_TABLE_VIEW_COLUMNS_PROGRESS, getTableViewColumnsWatcher);
  yield takeLatest(OverChargeClaimsTypes.GET_SORTING_FIELDS_PROGRESS, getSortingFieldsWatcher);
  yield takeLatest(OverChargeClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_PROGRESS, saveTableViewColumns);
  yield takeLatest(OverChargeClaimsTypes.GET_CLAIM_NOTES_PROGRESS, getClaimNotesWatcher);
  yield takeLatest(OverChargeClaimsTypes.GET_CLAIM_SETTLEMENTS_PROGRESS, getClaimSettlementsWatcher);
  yield takeLatest(OverChargeClaimsTypes.GET_CLAIM_ITEMS_PROGRESS, getClaimItemsWatcher);
  yield takeLatest(OverChargeClaimsTypes.APPLY_CLAIM_RECOVERY_PERCENT_PROGRESS, applyClaimRecoveryPercentWatcher);
}
